<template>
    <div ref="rootEl" class="flex flex-col gap-6">
        <header>
            <h2 v-if="currentPage?.topSection" class="body-bold text-ink mb-1">{{ currentPage.topSection.title }}</h2>
            <p v-if="currentPage?.topSection?.instructions" class="body-small text-ink-muted leading-relaxed mb-3">
                {{ currentPage.topSection.instructions }}
            </p>
            <div class="flex items-center justify-between details font-medium text-ink-muted mb-3">
                <span>{{ progressLabel }}</span>
            </div>
            <BaseProgressBar :value="progressPct" size="sm" :aria-label="t('components.assessment.progress', { percent: progressPct })" :color="accentColor" />
        </header>

        <div v-for="item in pageItems" :key="item.question.id" class="flex flex-col gap-3">
            <div v-if="item.subheading" class="details font-semibold uppercase tracking-wider text-ink-muted">{{ item.subheading }}</div>
            <QuestionCard
                :question="item.question"
                :score="item.question.saved.score"
                :text="textDrafts[item.question.id]"
                :not-observed="item.question.saved.not_observed"
                :allow-score-examples="allowScoreExamples"
                :allow-not-observed="allowNotObserved"
                :show-unanswered="showValidation && isRequiredUnanswered(item.question)"
                :accent-color="accentColor"
                :accent-surface-color="accentSurfaceColor"
                :save-error="saveErrors[item.question.id]"
                @update:score="setScore(item.question, $event)"
                @update:text="onTextInput(item.question, $event)"
                @update:not-observed="setNotObserved(item.question, $event)"
                @update:busy="setBusy(item.question.id, $event)"
                @blur="onTextBlur(item.question)" />
        </div>

        <BaseNotification v-if="error" theme="warning" :text="error" hide-dismiss />

        <div class="flex items-center justify-between">
            <BaseButton :disabled="saving || composerBusy" theme="tertiary" @click="goPrev">
                <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("components.assessment.back") }}
            </BaseButton>
            <BaseButton v-if="!isLastPage" theme="primary" :disabled="saving || composerBusy" class="inline-flex items-center gap-1" @click="goNext">
                {{ t("components.assessment.next") }} <i class="bi bi-arrow-right" aria-hidden="true"></i>
            </BaseButton>
            <BaseButton v-else theme="primary" :disabled="submitting || submitted || saving || composerBusy" @click="onSubmit">
                {{ submitting ? t("components.assessment.submitting") : t("components.assessment.submit") }}
            </BaseButton>
        </div>
        <div v-if="composerBusy" class="details text-ink-muted text-right -mt-3">{{ t("components.assessment.stopRecording") }}</div>

        <ConfirmationDialog
            v-model:open="showSubmitConfirm"
            :title="t('components.assessment.submitConfirmTitle')"
            :confirm-label="t('components.assessment.submitConfirmLabel')"
            :cancel-label="t('components.assessment.keepEditing')"
            :confirm-disabled="submitting"
            :close-on-confirm="false"
            @confirm="confirmSubmit">
            <template #content>
                <I18nT keypath="components.assessment.submitConfirmBody" tag="div" class="body text-ink-muted">
                    <template #emphasis>
                        <strong class="text-ink">{{ t("components.assessment.submitConfirmEmphasis") }}</strong>
                    </template>
                </I18nT>
            </template>
        </ConfirmationDialog>
    </div>
</template>

<script setup lang="ts">
import { fetchApi, useFetch } from "~vue/api";
import type { AssessmentDisplayMode, QuestionWithSaved, SectionSummary } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import QuestionCard from "~vue/components/QuestionCard.vue";
import {
    buildChunkedPages,
    buildPageItems,
    buildSectionPages,
    DEFAULT_QUESTIONS_PER_PAGE,
    type SectionPage,
    sectionsByCode as toSectionsByCode,
} from "~vue/utils/assessmentSections";
import { isRequiredUnanswered } from "~vue/utils/assessmentValidation";
import { isNetworkError } from "~vue/utils/threeSixty";
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { I18nT, useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
    responseId: string;
    questions: QuestionWithSaved[];
    sections: SectionSummary[];
    // How questions are split into pages. `paginated` chunks the list into
    // `questionsPerPage` pages and shows no section title, instructions or
    // sub-section eyebrow, because the participant must not be able to infer
    // the grouping. Anything else is one top-level section per page.
    displayMode?: AssessmentDisplayMode;
    questionsPerPage?: number;
    // LAB-798: forwarded to each QuestionCard to hide the score-example composer.
    allowScoreExamples?: boolean;
    // Forwarded to each QuestionCard to hide the "Not observed" tile.
    allowNotObserved: boolean;
    // Present only for unauthenticated external raters — appended to the
    // response-scoped calls so the backend can authorize via the invite token.
    inviteToken?: string;
    // Accent for the progress bar and score tiles, matching the host flow's
    // reflection family color. Callers pass a design-system token, not a hex.
    accentColor?: string;
    // Pastel fill for the selected score tile. Forwarded so a grouped template
    // renders the same accent as the one-per-page path in the same flow —
    // without it QuestionCard falls back to the full-strength accent.
    accentSurfaceColor?: string;
}>();

// External raters carry no JWT; the invite token is their entitlement on the
// response-scoped endpoints. Authenticated callers (self-assessment, internal
// raters) omit it and authorize via their session/JWT.
const inviteTokenQuery = computed(() => (props.inviteToken ? `?invite_token=${props.inviteToken}` : ""));

const emit = defineEmits<{
    // Fired after a successful submit. The host owns the redirect + analytics;
    // it reads the (mutated-in-place) question list it passed in for counts.
    submitted: [];
    // Fired when the rater clicks "Back" from the first section page.
    exit: [];
}>();

const rootEl = ref<HTMLElement | null>(null);
const currentPageIndex = ref(0);
const error = ref<string | null>(null);
const saving = ref(false);
const submitting = ref(false);
const showSubmitConfirm = ref(false);
// A submitted response is final, and the host may take a moment to unmount us.
// Latch it so neither the button nor the dialog can start a second submit.
const submitted = ref(false);

// Per-question text drafts, persisted-answer snapshots, and recording state.
// Props are populated at creation time (host renders this only after the
// payload loads), so we can seed synchronously.
const textDrafts = reactive<Record<string, string>>({});
const persisted = new Map<string, string>();
const busyIds = ref<Set<string>>(new Set());

// Long enough that a typed sentence is one request, short enough that a pause
// to think already persists the paragraph.
const TEXT_SAVE_DEBOUNCE_MS = 800;

// A section page holds every question in the section, so save-on-navigate puts
// the whole section at risk: each answer persists as it is made instead.
// `saveDirtyOnPage` stays as the pre-navigation safety net.
const saveErrors = reactive<Record<string, string>>({});
const textSaveTimers = new Map<string, ReturnType<typeof setTimeout>>();
const inFlightSaves = new Map<string, Promise<boolean>>();

function snapshot(q: QuestionWithSaved, text: string): string {
    return JSON.stringify({ score: q.saved.score, text, not_observed: q.saved.not_observed });
}

for (const q of props.questions) {
    textDrafts[q.id] = q.saved.text ?? "";
    persisted.set(q.id, snapshot(q, q.saved.text ?? ""));
}

/** Re-seed drafts and snapshots for answers that were written server-side. */
function adoptSavedAnswers(questionIds: string[]) {
    for (const q of props.questions.filter((question) => questionIds.includes(question.id))) {
        const text = q.saved.text ?? "";
        textDrafts[q.id] = text;
        persisted.set(q.id, snapshot(q, text));
    }
}

/** Jump to the final section page, so a filled assessment lands on Submit. */
function goToLastPage() {
    currentPageIndex.value = pages.value.length - 1;
    scrollToTop();
}

defineExpose({ adoptSavedAnswers, goToLastPage, saveDirtyOnPage });

const sectionsByCode = computed(() => toSectionsByCode(props.sections));
const hidesSections = computed(() => props.displayMode === "paginated");

const pages = computed<SectionPage<QuestionWithSaved>[]>(() =>
    hidesSections.value ? buildChunkedPages(props.questions, props.questionsPerPage ?? DEFAULT_QUESTIONS_PER_PAGE) : buildSectionPages(props.questions, sectionsByCode.value),
);

const currentPage = computed<SectionPage<QuestionWithSaved> | null>(() => pages.value[currentPageIndex.value] ?? null);
const isLastPage = computed(() => currentPageIndex.value === pages.value.length - 1);
const progressPct = computed(() => {
    if (pages.value.length === 0) {
        return 0;
    }
    return Math.round(((currentPageIndex.value + 1) / pages.value.length) * 100);
});
const progressLabel = computed(() => {
    const position = { current: currentPageIndex.value + 1, total: pages.value.length };
    return hidesSections.value ? t("components.assessment.pageOf", position) : t("components.assessment.sectionOf", position);
});

const pageItems = computed(() => {
    const questions = currentPage.value?.questions ?? [];
    if (hidesSections.value) {
        return questions.map((question) => ({ question, subheading: null }));
    }
    return buildPageItems(questions, sectionsByCode.value);
});

const composerBusy = computed(() => busyIds.value.size > 0);

function setBusy(id: string, busy: boolean) {
    const next = new Set(busyIds.value);
    if (busy) {
        next.add(id);
    } else {
        next.delete(id);
    }
    busyIds.value = next;
}

/**
 * Persist one question now, tracked so navigation can await it. Saves for the
 * same question chain rather than race, so the value the user ended on is the
 * value that lands.
 */
function autosave(q: QuestionWithSaved): Promise<boolean> {
    // Settle the predecessor before chaining: a rejected link would otherwise
    // make every later save for this question skip straight to the rejection,
    // so one thrown error would silently stop the question saving at all.
    const previous = (inFlightSaves.get(q.id) ?? Promise.resolve(true)).catch(() => false);
    const tracked: Promise<boolean> = previous
        .then(async () => {
            // A request that throws is still a save that did not happen, so it
            // reports like one. Without this the rejection escapes through
            // every `void autosave(...)` call site and, worse, through
            // settleAutosaves' Promise.all, taking Next and Submit with it.
            const failure = await saveQuestion(q).catch(() => t("components.assessment.saveFailed"));
            if (failure === null) {
                delete saveErrors[q.id];
            } else {
                saveErrors[q.id] = failure;
            }
            return failure === null;
        })
        .finally(() => {
            if (inFlightSaves.get(q.id) === tracked) {
                inFlightSaves.delete(q.id);
            }
        });
    inFlightSaves.set(q.id, tracked);
    return tracked;
}

function scheduleTextSave(q: QuestionWithSaved) {
    const existing = textSaveTimers.get(q.id);
    if (existing !== undefined) {
        clearTimeout(existing);
    }
    textSaveTimers.set(
        q.id,
        setTimeout(() => {
            textSaveTimers.delete(q.id);
            void autosave(q);
        }, TEXT_SAVE_DEBOUNCE_MS),
    );
}

/** Run a question's debounced save now, if one is waiting. */
function flushTextSave(q: QuestionWithSaved): Promise<boolean> | undefined {
    const timer = textSaveTimers.get(q.id);
    if (timer === undefined) {
        return undefined;
    }
    clearTimeout(timer);
    textSaveTimers.delete(q.id);
    return autosave(q);
}

function flushAllTextSaves() {
    for (const id of [...textSaveTimers.keys()]) {
        const q = props.questions.find((question) => question.id === id);
        if (q) {
            void flushTextSave(q);
        }
    }
}

/** Let every debounced and in-flight autosave settle. */
async function settleAutosaves() {
    flushAllTextSaves();
    // Chained saves can enqueue while we wait, so drain until nothing is left.
    // Nothing retries today, so one pass settles everything and the bound is
    // never reached; it is here so a future save path that does re-enqueue
    // cannot turn this into a hang.
    for (let i = 0; i < 10 && inFlightSaves.size > 0; i++) {
        await Promise.all([...inFlightSaves.values()]);
    }
}

function setScore(q: QuestionWithSaved, n: number) {
    q.saved = { ...q.saved, score: n, not_observed: false };
    void autosave(q);
}

function setNotObserved(q: QuestionWithSaved, v: boolean) {
    q.saved = { ...q.saved, score: v ? null : q.saved.score, not_observed: v };
    void autosave(q);
}

function onTextInput(q: QuestionWithSaved, text: string) {
    textDrafts[q.id] = text;
    scheduleTextSave(q);
}

function onTextBlur(q: QuestionWithSaved) {
    void flushTextSave(q);
}

function isDirty(q: QuestionWithSaved): boolean {
    return snapshot(q, textDrafts[q.id] ?? "") !== persisted.get(q.id);
}

const answersPath = computed(() => `/v1/reflections/responses/${props.responseId}/answers${inviteTokenQuery.value}`);

function answerPayload(q: QuestionWithSaved) {
    return {
        question_id: q.id,
        score: q.saved.score,
        text: textDrafts[q.id] ?? "",
        not_observed: q.saved.not_observed,
    };
}

/** Null when the answer is stored, otherwise the message to show on it. */
async function saveQuestion(q: QuestionWithSaved): Promise<string | null> {
    const text = textDrafts[q.id] ?? "";
    const { data, error: fetchError } = await useFetch(answersPath.value)
        .post(JSON.stringify(answerPayload(q)), "application/json")
        .json();
    if (fetchError.value) {
        // A refusal explains itself, and the explanation beats our own copy:
        // a response the backend has closed is not going to save on a retry,
        // so "Please try again." would be a lie.
        return (data.value as { error?: string } | null)?.error || t("components.assessment.saveFailed");
    }
    q.saved = { ...q.saved, text };
    persisted.set(q.id, snapshot(q, text));
    return null;
}

// Safety net before navigating or submitting: autosave has usually already
// persisted this page, so wait for anything debounced or on the wire and then
// retry only what is genuinely still unsaved (a failed autosave, say).
async function saveDirtyOnPage(): Promise<boolean> {
    await settleAutosaves();
    const dirty = (currentPage.value?.questions ?? []).filter(isDirty);
    if (dirty.length === 0) {
        return true;
    }
    saving.value = true;
    error.value = null;
    try {
        const results = await Promise.all(dirty.map((q) => autosave(q)));
        if (results.some((ok) => !ok)) {
            error.value = t("components.assessment.saveAllFailed");
            return false;
        }
        return true;
    } finally {
        saving.value = false;
    }
}

// Reset to the top on section change. The sidebar layout (self-assess) scrolls
// inside an Inertia [scroll-region] container; the rater flow scrolls the
// window — reset both so it works in either host.
function scrollToTop() {
    rootEl.value?.closest("[scroll-region]")?.scrollTo({ top: 0 });
    globalThis.scrollTo({ top: 0 });
}

// Sync the page's text drafts into each question's saved state so validation
// reflects what the user has typed (drafts are otherwise only persisted on save).
function syncDraftsOnCurrentPage() {
    for (const q of currentPage.value?.questions ?? []) {
        const draft = textDrafts[q.id] ?? "";
        if (draft !== q.saved.text) {
            q.saved = { ...q.saved, text: draft };
        }
    }
}

function unansweredOnCurrentPage(): QuestionWithSaved[] {
    return (currentPage.value?.questions ?? []).filter(isRequiredUnanswered);
}

/** Sync drafts and validate required questions on the current page. Returns true if valid. */
function validateCurrentPage(action: "continuing" | "submitting"): boolean {
    syncDraftsOnCurrentPage();
    const unanswered = unansweredOnCurrentPage();
    if (unanswered.length > 0) {
        showValidation.value = true;
        const count = unanswered.length;
        error.value =
            action === "continuing" ? t("components.assessment.requiredBeforeContinuing", { count }, count) : t("components.assessment.requiredBeforeSubmitting", { count }, count);
        return false;
    }
    showValidation.value = false;
    error.value = null;
    return true;
}

async function goNext() {
    if (isLastPage.value) {
        return;
    }
    if (!validateCurrentPage("continuing")) {
        return;
    }
    if (!(await saveDirtyOnPage())) {
        return;
    }
    currentPageIndex.value += 1;
    scrollToTop();
}

async function goPrev() {
    if (currentPageIndex.value === 0) {
        if (!(await saveDirtyOnPage())) {
            return;
        }
        emit("exit");
        return;
    }
    if (!(await saveDirtyOnPage())) {
        return;
    }
    currentPageIndex.value -= 1;
    scrollToTop();
}

const showValidation = ref(false);

/**
 * Best-effort persist for a page that is going away. `useFetch` has no way to
 * set `keepalive`, and `sendBeacon` can't carry the Authorization header the
 * answers endpoint needs from a signed-in participant, so this one path uses
 * `fetchApi` (the project's own wrapper) with `keepalive` instead.
 *
 * Browsers cap the aggregate keepalive payload at roughly 64 KB per page and
 * drop whatever exceeds it, so saving as you go is load-bearing here: it is
 * what keeps the dirty set small enough for this last flush to fit.
 */
function flushOnUnload() {
    for (const timer of textSaveTimers.values()) {
        clearTimeout(timer);
    }
    textSaveTimers.clear();
    for (const q of props.questions.filter(isDirty)) {
        void fetchApi(answersPath.value, { method: "POST", body: JSON.stringify(answerPayload(q)), keepalive: true });
    }
}

// A backgrounded tab may never come back, but it is still alive here, so the
// normal save path runs and its result is observable.
function onVisibilityChange() {
    if (document.visibilityState === "hidden") {
        flushAllTextSaves();
    }
}

onMounted(() => {
    document.addEventListener("visibilitychange", onVisibilityChange);
    globalThis.addEventListener("pagehide", flushOnUnload);
});

onBeforeUnmount(() => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    globalThis.removeEventListener("pagehide", flushOnUnload);
    for (const timer of textSaveTimers.values()) {
        clearTimeout(timer);
    }
    textSaveTimers.clear();
});

async function onSubmit() {
    if (submitting.value || submitted.value) {
        return;
    }
    if (!validateCurrentPage("submitting")) {
        return;
    }
    if (!(await saveDirtyOnPage())) {
        return;
    }
    showSubmitConfirm.value = true;
}

async function confirmSubmit() {
    if (submitting.value || submitted.value) {
        return;
    }
    submitting.value = true;
    try {
        const { error: fetchError, data } = await useFetch(`/v1/reflections/responses/${props.responseId}/submit${inviteTokenQuery.value}`).post().json();
        if (fetchError.value) {
            error.value = isNetworkError(fetchError.value)
                ? t("components.assessment.connectionSubmitFailed")
                : (data.value as { error?: string })?.error || t("components.assessment.submitFailed");
            return;
        }
        submitted.value = true;
        emit("submitted");
    } finally {
        submitting.value = false;
        // The dialog is `:close-on-confirm="false"`, so closing it is ours to do
        // on both paths — the host's redirect is not a reliable dismissal.
        showSubmitConfirm.value = false;
    }
}
</script>
